// Robust chemical sorting and filtering utility
// Sorts by display_order from sensor mappings, then alphabetically
// Ensures consistent sorting across the entire dashboard
//
// SINGLE SOURCE OF TRUTH: Sensor Mappings page controls which chemicals are displayed.
// A chemical is "active" if its chemical_name is set to anything other than "Unassigned".

/**
 * Extracts the numeric prefix from a chemical name (legacy, kept for backward compatibility)
 * @param {string} name - Chemical name that might have a numeric prefix
 * @returns {number} - The extracted number or Infinity if no number found
 */
export function extractChemicalNumber(name) {
  if (!name) return Infinity;

  // Matches various number formats at the start:
  // "1", "01", "1.", "01.", "1 ", "1. ", "01 - ", etc.
  const match = name.match(/^(\d+)[.\s-]*/);
  return match ? parseInt(match[1], 10) : Infinity;
}

/**
 * Sorts chemicals by display_order first, then alphabetically by name
 * @param {Array} items - Array of items with chemical_name or name property
 * @param {string} nameField - Field name containing the chemical name (default: 'chemical_name')
 * @returns {Array} - Sorted array
 */
export function sortChemicals(items, nameField = 'chemical_name') {
  if (!items || !Array.isArray(items)) return [];

  return [...items].sort((a, b) => {
    // First: Sort by display_order if available
    const aOrder = a.display_order ?? a.displayOrder ?? Infinity;
    const bOrder = b.display_order ?? b.displayOrder ?? Infinity;

    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }

    // Second: Fall back to alphabetical sorting by name
    const aName = a[nameField] || '';
    const bName = b[nameField] || '';
    return aName.localeCompare(bName);
  });
}

/**
 * Sorts an object of chemicals grouped by bay
 * @param {Object} chemicalsByBay - Object with bay keys and chemical arrays
 * @param {string} nameField - Field name containing the chemical name
 * @returns {Object} - Object with sorted arrays for each bay
 */
export function sortChemicalsByBay(chemicalsByBay, nameField = 'name') {
  const sorted = {};

  Object.keys(chemicalsByBay).forEach(bay => {
    sorted[bay] = sortChemicals(chemicalsByBay[bay], nameField);
  });

  return sorted;
}

/**
 * Checks if a sensor has an assigned chemical (controlled by Sensor Mappings page)
 * SINGLE SOURCE OF TRUTH: If chemical_name is "Unassigned" or empty, sensor is not active.
 *
 * @param {Object} sensor - Sensor object with chemical_name property
 * @returns {boolean} - True if sensor has an assigned chemical
 */
export function isAssignedSensor(sensor) {
  if (!sensor) return false;
  const name = sensor.chemical_name;
  if (!name || typeof name !== 'string') return false;
  const normalized = name.trim().toLowerCase();
  return normalized !== '' && normalized !== 'unassigned' && normalized !== 'not set';
}

/**
 * Checks if a chemical name represents an assigned/active chemical
 * Use this when you only have the chemical name string, not the full sensor object.
 *
 * @param {string} chemicalName - The chemical name to check
 * @returns {boolean} - True if the name represents an assigned chemical
 */
export function isAssignedChemical(chemicalName) {
  if (!chemicalName || typeof chemicalName !== 'string') return false;
  const normalized = chemicalName.trim().toLowerCase();
  return normalized !== '' && normalized !== 'unassigned' && normalized !== 'not set';
}

/**
 * Filters an array of sensors to only include those with assigned chemicals
 * SINGLE SOURCE OF TRUTH: Sensor Mappings page controls this via chemical_name field.
 *
 * @param {Array} sensors - Array of sensor objects
 * @returns {Array} - Filtered array with only assigned sensors
 */
export function filterAssignedSensors(sensors) {
  if (!sensors || !Array.isArray(sensors)) return [];
  return sensors.filter(isAssignedSensor);
}

/**
 * Gets unique chemical names from sensors, excluding unassigned ones
 *
 * @param {Array} sensors - Array of sensor objects
 * @returns {Array} - Array of unique assigned chemical names
 */
export function getAssignedChemicalNames(sensors) {
  if (!sensors || !Array.isArray(sensors)) return [];
  const names = new Set();
  sensors.forEach(sensor => {
    if (isAssignedSensor(sensor)) {
      names.add(sensor.chemical_name);
    }
  });
  return Array.from(names);
}

/**
 * Creates a unique display name for an injector
 * Format: "Chemical Name (Description)" or just "Chemical Name" if no description
 *
 * @param {Object} sensor - Sensor object with chemical_name and optional description
 * @returns {string} - Display name for the injector
 */
export function getInjectorDisplayName(sensor) {
  if (!sensor || !sensor.chemical_name) return 'Unknown';
  const name = sensor.chemical_name;
  const desc = sensor.description;
  return desc ? `${name} (${desc})` : name;
}

/**
 * Creates a unique key for an injector based on chip and sensor ID
 * Uses dot separator to match Firestore storage format (chip.sensor)
 *
 * @param {Object} sensor - Sensor object with chip/chipId and sensor_id/sensorId
 * @returns {string} - Unique key like "0004.sensor8"
 */
export function getInjectorKey(sensor) {
  if (!sensor) return 'unknown';
  const chip = sensor.chip || '';
  const sensorId = sensor.sensor_id || sensor.sensorId || '';
  return `${chip}.${sensorId}`;
}

/**
 * Builds a lookup map from injector key to sensor config
 * Used for mapping events (which have chip+sensor) to display names
 *
 * @param {Array} sensors - Array of sensor objects
 * @returns {Object} - Map of injector key → { displayName, chemical_name, description, display_order, bay }
 */
export function buildInjectorLookup(sensors) {
  if (!sensors || !Array.isArray(sensors)) return {};
  const lookup = {};

  sensors.forEach(sensor => {
    if (isAssignedSensor(sensor)) {
      const key = getInjectorKey(sensor);
      lookup[key] = {
        displayName: getInjectorDisplayName(sensor),
        chemical_name: sensor.chemical_name,
        description: sensor.description || '',
        display_order: sensor.display_order ?? sensor.displayOrder ?? Infinity,
        bay: sensor.bay
      };
    }
  });

  return lookup;
}

/**
 * Gets list of all assigned injectors with their display info
 * Each injector is treated as independent, even if they have the same chemical_name
 *
 * @param {Array} sensors - Array of sensor objects
 * @returns {Array} - Array of injector objects sorted by display_order
 */
export function getAssignedInjectors(sensors) {
  if (!sensors || !Array.isArray(sensors)) return [];

  const injectors = sensors
    .filter(isAssignedSensor)
    .map(sensor => ({
      key: getInjectorKey(sensor),
      displayName: getInjectorDisplayName(sensor),
      chemical_name: sensor.chemical_name,
      description: sensor.description || '',
      display_order: sensor.display_order ?? sensor.displayOrder ?? Infinity,
      bay: sensor.bay
    }));

  return sortChemicals(injectors, 'displayName');
}

/**
 * Gets unique chemicals with their display_order and description from sensors, excluding unassigned ones.
 * For chemicals that appear on multiple sensors, keeps the one with a display_order if available.
 *
 * @param {Array} sensors - Array of sensor objects
 * @returns {Array} - Array of objects: { chemical_name, display_order, description, bay }
 */
export function getAssignedChemicalsWithOrder(sensors) {
  if (!sensors || !Array.isArray(sensors)) return [];

  const chemicalMap = new Map(); // chemical_name -> { chemical_name, display_order, description, bay }

  sensors.forEach(sensor => {
    if (isAssignedSensor(sensor)) {
      const name = sensor.chemical_name;
      const existing = chemicalMap.get(name);

      // Keep the entry with display_order, or first occurrence if neither has it
      if (!existing || (sensor.display_order != null && existing.display_order == null)) {
        chemicalMap.set(name, {
          chemical_name: name,
          display_order: sensor.display_order ?? null,
          description: sensor.description ?? null,
          bay: sensor.bay
        });
      }
    }
  });

  return Array.from(chemicalMap.values());
}

/**
 * Formats a chemical name with its description suffix for display.
 * Example: "Lo Ph" with description "A1" becomes "Lo Ph (A1)"
 *
 * @param {string} chemicalName - The chemical name
 * @param {string|null} description - The description/location suffix
 * @returns {string} - Formatted display name
 */
export function formatChemicalDisplayName(chemicalName, description) {
  if (!chemicalName) return '';
  if (!description) return chemicalName;
  return `${chemicalName} (${description})`;
}

/**
 * Creates a lookup map from chemical_name to its display name (with description suffix).
 * Use this to convert stored chemical names to display names.
 *
 * @param {Array} sensors - Array of sensor objects
 * @returns {Map} - Map of chemical_name -> display_name
 */
export function createChemicalDisplayNameMap(sensors) {
  const map = new Map();
  const chemicalsWithOrder = getAssignedChemicalsWithOrder(sensors);

  chemicalsWithOrder.forEach(chem => {
    map.set(chem.chemical_name, formatChemicalDisplayName(chem.chemical_name, chem.description));
  });

  return map;
}
